import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Lock, LogOut, Plus, Trash2, Save, Key, FileText, Image, Calendar, Settings, HelpCircle, Check, Clock, Package, ArrowLeft, ShieldCheck } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('appointments');
  
  // Forgot/Change password states
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [identityForm, setIdentityForm] = useState({
    name: '',
    tc: '',
    seri_no: '',
    birth_date: '',
    old_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Data states
  const [appointments, setAppointments] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [dropdowns, setDropdowns] = useState({});
  const [siteContent, setSiteContent] = useState({});
  const [prices, setPrices] = useState([]);

  useEffect(() => {
    const savedToken = sessionStorage.getItem('admin_token');
    if (savedToken) verifyToken(savedToken);
  }, []);

  const verifyToken = async (t) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/verify`, { headers: { 'Authorization': `Bearer ${t}` } });
      if (res.ok) {
        setIsAuthenticated(true);
        setToken(t);
        loadAllData(t);
      } else {
        sessionStorage.removeItem('admin_token');
      }
    } catch { sessionStorage.removeItem('admin_token'); }
  };

  const loadAllData = async (t) => {
    const headers = { 'Authorization': `Bearer ${t}` };
    try {
      const [apptRes, sliderRes, dropdownRes, contentRes, priceRes, reviewRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/appointments`, { headers }),
        fetch(`${API_URL}/api/admin/sliders`, { headers }),
        fetch(`${API_URL}/api/admin/dropdowns`, { headers }),
        fetch(`${API_URL}/api/admin/content`, { headers }),
        fetch(`${API_URL}/api/admin/prices`, { headers }),
        fetch(`${API_URL}/api/admin/reviews`, { headers }),
      ]);
      
      // Hemen altındaki if bloğunun en sonuna da şunu ekle:
      if (reviewRes.ok) setReviews(await reviewRes.json());
      if (sliderRes.ok) setSliders(await sliderRes.json());
      if (apptRes.ok) setAppointments(await apptRes.json());
      if (dropdownRes.ok) {
        const data = await dropdownRes.json();
        const obj = {};
        data.forEach(d => obj[d.category] = d.options);
        setDropdowns(obj);
      }
      if (contentRes.ok) setSiteContent(await contentRes.json());
      if (priceRes.ok) setPrices(await priceRes.json());
    } catch (e) { console.error(e); }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setToken(data.token);
        sessionStorage.setItem('admin_token', data.token);
        setIsAuthenticated(true);
        loadAllData(data.token);
        toast.success('Giriş başarılı');
      } else {
        toast.error(data.detail || 'Giriş başarısız');
      }
    } catch { toast.error('Bağlantı hatası'); }
    setIsLoading(false);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setToken('');
  };

  const resetIdentityForm = () => {
    setIdentityForm({
      name: '',
      tc: '',
      seri_no: '',
      birth_date: '',
      old_password: '',
      new_password: '',
      confirm_password: ''
    });
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (identityForm.new_password !== identityForm.confirm_password) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    
    if (identityForm.new_password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }
    
    setIsVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: identityForm.name,
          tc: identityForm.tc,
          seri_no: identityForm.seri_no,
          birth_date: identityForm.birth_date,
          new_password: identityForm.new_password
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Şifre başarıyla sıfırlandı! Yeni şifrenizle giriş yapabilirsiniz.');
        setShowForgotPassword(false);
        resetIdentityForm();
      } else {
        toast.error(data.detail || 'Şifre sıfırlama başarısız');
      }
    } catch { toast.error('Bağlantı hatası'); }
    setIsVerifying(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (identityForm.new_password !== identityForm.confirm_password) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }
    
    if (identityForm.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalıdır');
      return;
    }
    
    setIsVerifying(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: identityForm.name,
          tc: identityForm.tc,
          seri_no: identityForm.seri_no,
          birth_date: identityForm.birth_date,
          old_password: identityForm.old_password,
          new_password: identityForm.new_password
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success('Şifre başarıyla değiştirildi!');
        setShowChangePassword(false);
        resetIdentityForm();
      } else {
        toast.error(data.detail || 'Şifre değiştirme başarısız');
      }
    } catch { toast.error('Bağlantı hatası'); }
    setIsVerifying(false);
  };

  // Forgot Password Screen
  if (showForgotPassword) {
    return (
      <>
        <Helmet><meta name="robots" content="noindex, nofollow" /><title>Şifremi Unuttum | Teslapro</title></Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <div className="bg-card rounded-2xl border shadow-card p-8">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mx-auto mb-4">
                  <ShieldCheck className="w-8 h-8 text-amber-500" />
                </div>
                <h1 className="text-2xl font-bold">Şifremi Unuttum</h1>
                <p className="text-sm text-muted-foreground mt-2">Kimlik bilgilerinizi doğrulayın</p>
              </div>
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label>İsim Soyisim *</Label>
                  <Input 
                    value={identityForm.name} 
                    onChange={(e) => setIdentityForm({...identityForm, name: e.target.value})} 
                    placeholder="Lütfen İsim/Soyisim" 
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label>TC Kimlik No *</Label>
                  <Input 
                    value={identityForm.tc} 
                    onChange={(e) => setIdentityForm({...identityForm, tc: e.target.value})} 
                    placeholder="XXXXXXXXXXX" 
                    maxLength={11}
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Kimlik Seri No *</Label>
                  <Input 
                    value={identityForm.seri_no} 
                    onChange={(e) => setIdentityForm({...identityForm, seri_no: e.target.value})} 
                    placeholder="AXXXXXXXX" 
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Doğum Tarihi *</Label>
                  <Input 
                    value={identityForm.birth_date} 
                    onChange={(e) => setIdentityForm({...identityForm, birth_date: e.target.value})} 
                    placeholder="GG.AA.YYYY" 
                    required 
                  />
                </div>
                <hr className="my-4" />
                <div className="space-y-2">
                  <Label>Yeni Şifre *</Label>
                  <Input 
                    type="password"
                    value={identityForm.new_password} 
                    onChange={(e) => setIdentityForm({...identityForm, new_password: e.target.value})} 
                    placeholder="En az 6 karakter" 
                    required 
                  />
                </div>
                <div className="space-y-2">
                  <Label>Yeni Şifre (Tekrar) *</Label>
                  <Input 
                    type="password"
                    value={identityForm.confirm_password} 
                    onChange={(e) => setIdentityForm({...identityForm, confirm_password: e.target.value})} 
                    placeholder="Şifreyi tekrar girin" 
                    required 
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="flex-1"
                    onClick={() => { setShowForgotPassword(false); resetIdentityForm(); }}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />Geri
                  </Button>
                  <Button type="submit" className="flex-1" disabled={isVerifying}>
                    {isVerifying ? 'Doğrulanıyor...' : 'Şifreyi Sıfırla'}
                  </Button>
                </div>
              </form>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <>
        <Helmet><meta name="robots" content="noindex, nofollow" /><title>Admin | Teslapro</title></Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
            <div className="bg-card rounded-2xl border shadow-card p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-primary" />
                </div>
                <h1 className="text-2xl font-bold">Admin Paneli</h1>
              </div>
              <form onSubmit={handleLogin} className="space-y-4">
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Şifre" required />
                <Button type="submit" className="w-full" disabled={isLoading}>{isLoading ? 'Giriş yapılıyor...' : 'Giriş Yap'}</Button>
              </form>
              <div className="mt-4 text-center">
                <Button 
                  variant="link" 
                  className="text-sm text-muted-foreground hover:text-primary"
                  onClick={() => setShowForgotPassword(true)}
                >
                  Şifremi Unuttum
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet><meta name="robots" content="noindex, nofollow" /><title>Admin | Teslapro</title></Helmet>
      <div className="min-h-screen bg-background">
        <header className="bg-card border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <h1 className="font-bold">Teslapro Admin</h1>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowChangePassword(true)}><Key className="w-4 h-4 mr-2" />Şifre Değiştir</Button>
              <Button variant="outline" size="sm" onClick={handleLogout}><LogOut className="w-4 h-4 mr-2" />Çıkış</Button>
            </div>
          </div>
        </header>

        <main className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex flex-wrap gap-2 mb-8">
            <Button variant={activeTab === 'appointments' ? 'default' : 'outline'} onClick={() => setActiveTab('appointments')}><Calendar className="w-4 h-4 mr-2" />Servis Kayıtları</Button>
            <Button variant={activeTab === 'sliders' ? 'default' : 'outline'} onClick={() => setActiveTab('sliders')}><Image className="w-4 h-4 mr-2" />Slider</Button>
            <Button variant={activeTab === 'dropdowns' ? 'default' : 'outline'} onClick={() => setActiveTab('dropdowns')}><Settings className="w-4 h-4 mr-2" />Dropdown</Button>
            <Button variant={activeTab === 'faq' ? 'default' : 'outline'} onClick={() => setActiveTab('faq')}><HelpCircle className="w-4 h-4 mr-2" />SSS</Button>
            <Button variant={activeTab === 'content' ? 'default' : 'outline'} onClick={() => setActiveTab('content')}><FileText className="w-4 h-4 mr-2" />İçerik</Button>
            <Button variant={activeTab === 'prices' ? 'default' : 'outline'} onClick={() => setActiveTab('prices')}><Package className="w-4 h-4 mr-2" />Fiyatlar</Button>
            <Button variant={activeTab === 'reviews' ? 'default' : 'outline'} onClick={() => setActiveTab('reviews')}><ShieldCheck className="w-4 h-4 mr-2" />Yorumlar</Button>
          </div>

          {activeTab === 'appointments' && <AppointmentsTab appointments={appointments} token={token} onRefresh={() => loadAllData(token)} />}
          {activeTab === 'sliders' && <SlidersTab sliders={sliders} token={token} onRefresh={() => loadAllData(token)} />}
          {activeTab === 'dropdowns' && <DropdownsTab dropdowns={dropdowns} token={token} onRefresh={() => loadAllData(token)} />}
          {activeTab === 'faq' && <FAQTab content={siteContent} token={token} onRefresh={() => loadAllData(token)} />}
          {activeTab === 'content' && <ContentTab content={siteContent} token={token} onRefresh={() => loadAllData(token)} />}
          {activeTab === 'prices' && <PricesTab prices={prices} token={token} onRefresh={() => loadAllData(token)} dropdowns={dropdowns} />}
          {activeTab === 'reviews' && <ReviewsTab reviews={reviews} token={token} onRefresh={() => loadAllData(token)} />}
        </main>
      </div>
      
      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        isOpen={showChangePassword}
        onClose={() => { setShowChangePassword(false); resetIdentityForm(); }}
        token={token}
        identityForm={identityForm}
        setIdentityForm={setIdentityForm}
        isVerifying={isVerifying}
        onSubmit={handleChangePassword}
      />
    </>
  );
};

// Appointments Tab
const AppointmentsTab = ({ appointments, token, onRefresh }) => {
  const updateStatus = async (id, status) => {
    await fetch(`${API_URL}/api/admin/appointments/${id}/status?status=${status}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    
    // Auto-delete when status is "Teslim Edildi"
    if (status === 'Teslim Edildi') {
      toast.success('Kayıt teslim edildi ve silindi');
      await fetch(`${API_URL}/api/admin/appointments/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
    } else {
      toast.success('Durum güncellendi');
    }
    onRefresh();
  };

  const deleteAppointment = async (id) => {
    if (!window.confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    await fetch(`${API_URL}/api/admin/appointments/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    toast.success('Kayıt silindi');
    onRefresh();
  };

  const statusColors = { 'Yeni': 'bg-blue-100 text-blue-800', 'İşlemde': 'bg-yellow-100 text-yellow-800', 'Teslim Edildi': 'bg-green-100 text-green-800' };

  return (
    <div className="bg-card rounded-2xl border p-6">
      <h2 className="text-xl font-semibold mb-4">Servis Kayıtları ({appointments.length})</h2>
      {appointments.length === 0 ? <p className="text-muted-foreground text-center py-8">Henüz kayıt yok</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b">
              <th className="text-left py-2 px-2">Kod</th>
              <th className="text-left py-2 px-2">Ad Soyad</th>
              <th className="text-left py-2 px-2">Telefon</th>
              <th className="text-left py-2 px-2">Cihaz</th>
              <th className="text-left py-2 px-2">Arıza</th>
              <th className="text-left py-2 px-2">Durum</th>
              <th className="text-center py-2 px-2">İşlem</th>
            </tr></thead>
            <tbody>
              {appointments.map((a) => (
                <tr key={a.id} className="border-b hover:bg-muted/50">
                  <td className="py-2 px-2 font-mono text-xs">{a.service_code}</td>
                  <td className="py-2 px-2">{a.name}</td>
                  <td className="py-2 px-2">{a.phone}</td>
                  <td className="py-2 px-2">{a.device_model}</td>
                  <td className="py-2 px-2">{a.issue_type}</td>
                  <td className="py-2 px-2">
                    <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                      <SelectTrigger className={`h-8 text-xs ${statusColors[a.status] || ''}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yeni">Yeni</SelectItem>
                        <SelectItem value="İşlemde">İşlemde</SelectItem>
                        <SelectItem value="Teslim Edildi">Teslim Edildi</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="py-2 px-2 text-center">
                    <Button variant="ghost" size="sm" onClick={() => deleteAppointment(a.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Sliders Tab
const SlidersTab = ({ sliders, token, onRefresh }) => {
  const [form, setForm] = useState({ id: null, image_url: '', title: '', subtitle: '', button_text: 'Fiyat Al', button_link: '/servis', order: 1, active: true });
  const [isEditing, setIsEditing] = useState(false);

  const saveSlider = async () => {
    if (!form.image_url || !form.title) { toast.error('Görsel URL ve başlık gerekli'); return; }
    
    // Eğer düzenleme modundaysak PUT (güncelleme), yeni ekliyorsak POST (yeni kayıt) atacak
    const url = isEditing ? `${API_URL}/api/admin/sliders/${form.id}` : `${API_URL}/api/admin/sliders`;
    const method = isEditing ? 'PUT' : 'POST';
    const payload = isEditing ? form : { ...form, id: Date.now().toString() };

    await fetch(url, {
      method: method,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify(payload)
    });
    
    toast.success(isEditing ? 'Slider güncellendi' : 'Slider eklendi');
    cancelEdit(); // Kaydettikten sonra formu temizle
    onRefresh();
  };

  // Slider'a tıklandığında bilgileri yukarıdaki forma dolduran fonksiyon
  const editSlider = (slider) => {
    setForm(slider);
    setIsEditing(true);
  };

  // Düzenlemekten vazgeçildiğinde formu sıfırlayan fonksiyon
  const cancelEdit = () => {
    setForm({ id: null, image_url: '', title: '', subtitle: '', button_text: 'Fiyat Al', button_link: '/servis', order: 1, active: true });
    setIsEditing(false);
  };

  const deleteSlider = async (id, e) => {
    e.stopPropagation(); // Sil tuşuna basınca aynı zamanda düzenleme modunu tetiklemesin diye
    if (!window.confirm('Bu sliderı silmek istediğinize emin misiniz?')) return;
    await fetch(`${API_URL}/api/admin/sliders/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    toast.success('Slider silindi');
    if (form.id === id) cancelEdit(); // Eğer sildiğimiz şeyi o an düzenliyorsak iptal et
    onRefresh();
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-2xl border p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold">{isEditing ? 'Slider Düzenle' : 'Yeni Slider Ekle'}</h3>
          {isEditing && (
            <Button variant="outline" size="sm" onClick={cancelEdit}>İptal</Button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Görsel URL</Label><Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="https://..." /></div>
          <div><Label>Başlık</Label><Input value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} /></div>
          <div><Label>Alt Başlık</Label><Input value={form.subtitle} onChange={(e) => setForm({...form, subtitle: e.target.value})} /></div>
          <div><Label>Buton Metni</Label><Input value={form.button_text} onChange={(e) => setForm({...form, button_text: e.target.value})} /></div>
          <div><Label>Buton Linki</Label><Input value={form.button_link} onChange={(e) => setForm({...form, button_link: e.target.value})} /></div>
          <div><Label>Sıra</Label><Input type="number" value={form.order} onChange={(e) => setForm({...form, order: parseInt(e.target.value)})} /></div>
        </div>
        <Button onClick={saveSlider} className="mt-4">
          {isEditing ? <><Save className="w-4 h-4 mr-2" />Güncelle</> : <><Plus className="w-4 h-4 mr-2" />Ekle</>}
        </Button>
      </div>
      <div className="bg-card rounded-2xl border p-6">
        <h3 className="font-semibold mb-2">Mevcut Sliderlar</h3>
        <p className="text-sm text-muted-foreground mb-4">Düzenlemek için satıra tıklayın</p>
        <div className="grid gap-4">
          {sliders.map((s) => (
            <div 
              key={s.id} 
              className={`flex items-center gap-4 p-4 border rounded-lg cursor-pointer transition-colors ${form.id === s.id ? 'bg-primary/10 border-primary' : 'hover:bg-muted/50'}`}
              onClick={() => editSlider(s)}
            >
              <img src={s.image_url} alt="" className="w-24 h-16 object-cover rounded" />
              <div className="flex-1"><p className="font-medium">{s.title}</p><p className="text-sm text-muted-foreground">{s.subtitle}</p></div>
              <Button variant="ghost" size="sm" onClick={(e) => deleteSlider(s.id, e)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Dropdowns Tab - Hierarchical dropdown management
const DropdownsTab = ({ dropdowns, token, onRefresh }) => {
  const [category, setCategory] = useState('product_types');
  const [options, setOptions] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  // Load options when category or dropdowns change
  useEffect(() => { 
    const categoryOptions = dropdowns[category];
    if (categoryOptions && Array.isArray(categoryOptions)) {
      setOptions([...categoryOptions]);
    } else {
      setOptions([]);
    }
  }, [category, dropdowns]);

  const handleCategoryChange = (newCategory) => {
    setCategory(newCategory);
  };

  const addOption = () => {
    const needsParentField = ['brands', 'models', 'issues'].includes(category);
    const isIssue = category === 'issues';
    const newOption = { 
      value: '', 
      label: '', 
      ...(needsParentField ? { parent: '' } : {}),
      ...(isIssue ? { has_quality_option: true } : {})
    };
    setOptions(prev => [...prev, newOption]);
  };
  
  const removeOption = (index) => {
    setOptions(prev => prev.filter((_, i) => i !== index));
  };
  
  const updateOption = (index, field, val) => {
    setOptions(prev => {
      const newOpts = [...prev];
      newOpts[index] = { ...newOpts[index], [field]: val };
      return newOpts;
    });
  };

  const saveDropdown = async () => {
    const validOptions = options.filter(o => o.value && o.label);
    if (validOptions.length === 0) {
      toast.error('En az bir geçerli seçenek ekleyin');
      return;
    }
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/dropdowns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ category, options: validOptions })
      });
      
      if (response.ok) {
        toast.success('Dropdown kaydedildi');
        await onRefresh();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Kaydetme başarısız');
      }
    } catch (e) {
      toast.error('Bağlantı hatası');
      console.error(e);
    }
    setIsSaving(false);
  };

  const categoryLabels = { 
    product_types: 'Ürün Türleri', 
    brands: 'Markalar', 
    models: 'Modeller', 
    issues: 'Arıza Türleri', 
    cities: 'Şehirler', 
    part_quality: 'Parça Kalitesi' 
  };

  const getParentOptions = () => {
    if (category === 'brands') return dropdowns['product_types'] || [];
    if (category === 'models') return dropdowns['brands'] || [];
    if (category === 'issues') return dropdowns['product_types'] || [];
    return [];
  };

  const needsParent = ['brands', 'models', 'issues'].includes(category);
  const isIssueCategory = category === 'issues';
  const parentLabel = category === 'brands' ? 'Ürün Türü' : category === 'models' ? 'Marka' : category === 'issues' ? 'Ürün Türü' : '';

  return (
    <div className="bg-card rounded-2xl border p-6">
      <div className="flex flex-wrap gap-2 mb-6">
        {Object.entries(categoryLabels).map(([key, label]) => (
          <Button key={key} variant={category === key ? 'default' : 'outline'} size="sm" onClick={() => handleCategoryChange(key)}>{label}</Button>
        ))}
      </div>
      
      {needsParent && (
        <p className="text-sm text-muted-foreground mb-4 p-3 bg-muted rounded-lg">
          {category === 'brands' ? 'Her marka bir ürün türüne bağlı olmalıdır (örn: Apple → Cep Telefonu)' : 
           category === 'models' ? 'Her model bir markaya bağlı olmalıdır (örn: iPhone 15 → Apple)' :
           category === 'issues' ? 'Her arıza türü bir ürün türüne bağlı olmalıdır (örn: Hall Sensör → Laptop)' : ''}
        </p>
      )}
      
      {isIssueCategory && (
        <div className="text-sm text-muted-foreground mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
          <p><strong>Ürün Türü:</strong> Arıza türünün hangi ürün türünde görüneceğini seçin. Boş bırakılırsa tüm ürünlerde görünür.</p>
          <p><strong>Hariç Markalar:</strong> Bu arızanın görünmemesini istediğiniz markaları seçin. Örn: Xiaomi seçilirse, Xiaomi telefonlarında bu arıza görünmez.</p>
          <p><strong>Yan Sanayi/Orijinal:</strong> Tik işaretli arızalarda müşteri parça kalitesi seçebilir.</p>
        </div>
      )}
      
      <div className="space-y-3">
        {options.map((opt, i) => (
          <div key={i} className="flex gap-2 items-start flex-wrap border-b pb-3">
            <div className="flex gap-2 items-center flex-wrap flex-1">
              {needsParent && (
                <Select value={opt.parent || '_all_'} onValueChange={(v) => updateOption(i, 'parent', v === '_all_' ? '' : v)}>
                  <SelectTrigger className="w-40"><SelectValue placeholder={parentLabel} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all_">Tümü</SelectItem>
                    {getParentOptions().map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              <Input placeholder="Değer (value)" value={opt.value || ''} onChange={(e) => updateOption(i, 'value', e.target.value)} className="flex-1 min-w-[120px]" />
              <Input placeholder="Görünen (label)" value={opt.label || ''} onChange={(e) => updateOption(i, 'label', e.target.value)} className="flex-1 min-w-[120px]" />
              {isIssueCategory && (
                <>
                  <Select 
                    value={opt.excluded_brands || '_none_'} 
                    onValueChange={(v) => {
                      const current = opt.excluded_brands ? opt.excluded_brands.split(',') : [];
                      if (v === '_none_') {
                        updateOption(i, 'excluded_brands', '');
                      } else if (current.includes(v)) {
                        updateOption(i, 'excluded_brands', current.filter(b => b !== v).join(','));
                      } else {
                        updateOption(i, 'excluded_brands', [...current, v].filter(Boolean).join(','));
                      }
                    }}
                  >
                    <SelectTrigger className="w-44">
                      <SelectValue placeholder="Hariç Markalar">
                        {opt.excluded_brands ? `${opt.excluded_brands.split(',').length} marka hariç` : 'Hariç Marka Yok'}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none_">Hariç Marka Yok</SelectItem>
                      {(dropdowns['brands'] || []).map((b) => (
                        <SelectItem key={b.value} value={b.value}>
                          {(opt.excluded_brands || '').split(',').includes(b.value) ? '✓ ' : ''}{b.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 text-sm whitespace-nowrap cursor-pointer px-2">
                    <input 
                      type="checkbox" 
                      checked={opt.has_quality_option !== false} 
                      onChange={(e) => updateOption(i, 'has_quality_option', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <span className="text-muted-foreground">Yan Sanayi/Orijinal</span>
                  </label>
                </>
              )}
              <Button variant="ghost" size="icon" onClick={() => removeOption(i)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-4">
        <Button variant="outline" onClick={addOption}><Plus className="w-4 h-4 mr-2" />Seçenek Ekle</Button>
        <Button onClick={saveDropdown} disabled={isSaving}><Save className="w-4 h-4 mr-2" />{isSaving ? 'Kaydediliyor...' : 'Kaydet'}</Button>
      </div>
    </div>
  );
};

// FAQ Tab
const FAQTab = ({ content, token, onRefresh }) => {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => { 
    const faqItems = content?.faq?.items || content?.faq || [];
    setItems(Array.isArray(faqItems) ? [...faqItems] : []);
  }, [content]);

  const addItem = () => setItems([...items, { question: '', answer: '' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, val) => {
    const newItems = [...items];
    newItems[i][field] = val;
    setItems(newItems);
  };

  const saveFAQ = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/content`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ section: 'faq', content: { items } })
      });
      if (res.ok) {
        toast.success('SSS kaydedildi');
        onRefresh();
      } else {
        toast.error('Kaydetme başarısız');
      }
    } catch (e) {
      toast.error('Bağlantı hatası');
    }
    setIsLoading(false);
  };

  return (
    <div className="bg-card rounded-2xl border p-6">
      <h3 className="font-semibold mb-4">Sıkça Sorulan Sorular ({items.length} soru)</h3>
      <div className="space-y-4">
        {items.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">Henüz soru eklenmemiş. Yeni soru eklemek için aşağıdaki butonu kullanın.</p>
        ) : (
          items.map((item, i) => (
            <div key={i} className="p-4 border rounded-lg space-y-2 bg-muted/30">
              <div className="flex gap-2">
                <div className="flex-1">
                  <Label className="text-xs text-muted-foreground">Soru {i + 1}</Label>
                  <Input placeholder="Soru" value={item.question || ''} onChange={(e) => updateItem(i, 'question', e.target.value)} />
                </div>
                <Button variant="ghost" size="icon" onClick={() => removeItem(i)} className="mt-5"><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Cevap</Label>
                <Textarea placeholder="Cevap" value={item.answer || ''} onChange={(e) => updateItem(i, 'answer', e.target.value)} rows={2} />
              </div>
            </div>
          ))
        )}
      </div>
      <div className="flex gap-2 mt-4">
        <Button variant="outline" onClick={addItem}><Plus className="w-4 h-4 mr-2" />Soru Ekle</Button>
        <Button onClick={saveFAQ} disabled={isLoading}><Save className="w-4 h-4 mr-2" />{isLoading ? 'Kaydediliyor...' : 'Kaydet'}</Button>
      </div>
    </div>
  );
};

// Content Tab
const ContentTab = ({ content, token, onRefresh }) => {
  const [section, setSection] = useState('homepage');
  const [data, setData] = useState({});

  useEffect(() => { setData(content[section] || {}); }, [section, content]);

  const saveContent = async () => {
    await fetch(`${API_URL}/api/admin/content`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ section, content: data })
    });
    toast.success('İçerik kaydedildi');
    onRefresh();
  };

  const sections = { homepage: 'Ana Sayfa', about: 'Hakkımızda', contact: 'İletişim', seo: 'SEO' };
  const fields = {
    homepage: [{ key: 'hero_title', label: 'Hero Başlık' }, { key: 'hero_subtitle', label: 'Alt Başlık' }, { key: 'hero_cta', label: 'Buton' }],
    about: [{ key: 'title', label: 'Başlık' }, { key: 'subtitle', label: 'Alt Başlık' }, { key: 'history', label: 'Hikaye', textarea: true }],
    contact: [{ key: 'phone', label: 'Telefon' }, { key: 'email', label: 'E-posta' }, { key: 'address', label: 'Adres' }, { key: 'working_hours', label: 'Çalışma Saatleri' }],
    seo: [{ key: 'title', label: 'Meta Title' }, { key: 'description', label: 'Meta Description', textarea: true }, { key: 'keywords', label: 'Anahtar Kelimeler' }],
  };

  return (
    <div className="bg-card rounded-2xl border p-6">
      <div className="flex gap-2 mb-6">
        {Object.entries(sections).map(([k, v]) => <Button key={k} variant={section === k ? 'default' : 'outline'} size="sm" onClick={() => setSection(k)}>{v}</Button>)}
      </div>
      <div className="space-y-4">
        {fields[section]?.map((f) => (
          <div key={f.key}>
            <Label>{f.label}</Label>
            {f.textarea ? <Textarea value={data[f.key] || ''} onChange={(e) => setData({...data, [f.key]: e.target.value})} rows={3} /> : <Input value={data[f.key] || ''} onChange={(e) => setData({...data, [f.key]: e.target.value})} />}
          </div>
        ))}
      </div>
      <Button onClick={saveContent} className="mt-4"><Save className="w-4 h-4 mr-2" />Kaydet</Button>
    </div>
  );
};

// Prices Tab - with dropdowns linked to Dropdown management
const PricesTab = ({ prices, token, onRefresh, dropdowns }) => {
  const [form, setForm] = useState({ id: null, product_type: '', brand: '', model: '', issue: '', aftermarket_price: '', original_price: '' });
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [filteredModels, setFilteredModels] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  const productTypes = dropdowns['product_types'] || [];
  const allBrands = dropdowns['brands'] || [];
  const allModels = dropdowns['models'] || [];
  const issues = dropdowns['issues'] || [];

  // Handle product type change - reset dependent fields and filter brands
  const handleProductTypeChange = (value) => {
    setForm(prev => ({ ...prev, product_type: value, brand: '', model: '' }));
    const filtered = allBrands.filter(b => b.parent === value);
    setFilteredBrands(filtered);
    setFilteredModels([]);
  };

  // Handle brand change - reset model and filter models
  const handleBrandChange = (value) => {
    setForm(prev => ({ ...prev, brand: value, model: '' }));
    const filtered = allModels.filter(m => m.parent === value);
    setFilteredModels(filtered);
  };

  // Edit existing price
  const editPrice = (price) => {
    // Find matching dropdown values
    const productType = productTypes.find(p => p.label === price.product_type)?.value || '';
    const brand = allBrands.find(b => b.label === price.brand)?.value || '';
    const model = allModels.find(m => m.label === price.model)?.value || '';
    const issue = issues.find(i => i.label === price.issue)?.value || '';
    
    // Set filtered options
    if (productType) {
      setFilteredBrands(allBrands.filter(b => b.parent === productType));
    }
    if (brand) {
      setFilteredModels(allModels.filter(m => m.parent === brand));
    }
    
    setForm({
      id: price.id,
      product_type: productType,
      brand: brand,
      model: model,
      issue: issue,
      aftermarket_price: price.aftermarket_price || '',
      original_price: price.original_price || ''
    });
    setIsEditing(true);
  };

  // Cancel editing
  const cancelEdit = () => {
    setForm({ id: null, product_type: '', brand: '', model: '', issue: '', aftermarket_price: '', original_price: '' });
    setFilteredBrands([]);
    setFilteredModels([]);
    setIsEditing(false);
  };

const savePrice = async () => {
  if (!form.product_type || !form.brand || !form.model || !form.issue) {
    toast.error('Tüm alanları doldurun');
    return;
  }

  const productLabel =
    productTypes.find((p) => p.value === form.product_type)?.label || form.product_type;
  const brandLabel =
    filteredBrands.find((b) => b.value === form.brand)?.label || form.brand;
  const modelLabel =
    filteredModels.find((m) => m.value === form.model)?.label || form.model;
  const issueLabel =
    issues.find((i) => i.value === form.issue)?.label || form.issue;

  const payload = {
    product_type: productLabel,
    brand: brandLabel,
    model: modelLabel,
    issue: issueLabel,
    aftermarket_price: form.aftermarket_price?.trim?.() ?? form.aftermarket_price,
    original_price: form.original_price?.trim?.() ?? form.original_price,
  };

  if (isEditing && form.id) {
    payload.id = form.id;
  }

  try {
    const response = await fetch(`${API_URL}/api/admin/prices`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        toast.error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        window.location.reload();
        return;
      }

      throw new Error(data.detail || data.message || 'Fiyat kaydedilemedi');
    }

    toast.success(data.message || (isEditing ? 'Fiyat güncellendi' : 'Fiyat eklendi'));

    setForm({
      id: null,
      product_type: '',
      brand: '',
      model: '',
      issue: '',
      aftermarket_price: '',
      original_price: '',
    });
    setFilteredBrands([]);
    setFilteredModels([]);
    setIsEditing(false);
    onRefresh();
  } catch (error) {
    console.error('savePrice error:', error);
    toast.error(error.message || 'Bağlantı hatası');
  }
};

const deletePrice = async (id) => {
  if (!window.confirm('Bu fiyatı silmek istediğinize emin misiniz?')) return;

  try {
    const response = await fetch(`${API_URL}/api/admin/prices/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    let data = {};
    try {
      data = await response.json();
    } catch {
      data = {};
    }

    if (!response.ok) {
      if (response.status === 401) {
        sessionStorage.removeItem('admin_token');
        toast.error('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        window.location.reload();
        return;
      }

      throw new Error(data.detail || data.message || 'Fiyat silinemedi');
    }

    toast.success('Fiyat silindi');
    onRefresh();
  } catch (error) {
    console.error('deletePrice error:', error);
    toast.error(error.message || 'Bağlantı hatası');
  }
};

  // Check if selected issue has quality option
  const selectedIssue = issues.find(i => i.value === form.issue);
  const hasQualityOption = selectedIssue?.has_quality_option !== false;

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-2xl border p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="font-semibold">{isEditing ? 'Fiyat Düzenle' : 'Yeni Fiyat Ekle'}</h3>
          {isEditing && (
            <Button variant="outline" size="sm" onClick={cancelEdit}>İptal</Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Fiyat alanına rakam (2500), aralık (2500-5000) veya metin (Arıza Tespiti Gereklidir) yazabilirsiniz.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Product Type */}
          <div className="space-y-2">
            <Label>Ürün Türü</Label>
            <Select value={form.product_type} onValueChange={handleProductTypeChange}>
              <SelectTrigger><SelectValue placeholder="Ürün türü seçin" /></SelectTrigger>
              <SelectContent>
                {productTypes.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          {/* Brand */}
          <div className="space-y-2">
            <Label>Marka</Label>
            <Select value={form.brand} onValueChange={handleBrandChange} disabled={!form.product_type}>
              <SelectTrigger><SelectValue placeholder={form.product_type ? "Marka seçin" : "Önce ürün türü seçin"} /></SelectTrigger>
              <SelectContent>
                {filteredBrands.map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          {/* Model */}
          <div className="space-y-2">
            <Label>Model</Label>
            <Select value={form.model} onValueChange={(v) => setForm({...form, model: v})} disabled={!form.brand}>
              <SelectTrigger><SelectValue placeholder={form.brand ? "Model seçin" : "Önce marka seçin"} /></SelectTrigger>
              <SelectContent>
                {filteredModels.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          {/* Issue */}
          <div className="space-y-2">
            <Label>Arıza Türü</Label>
            <Select value={form.issue} onValueChange={(v) => setForm({...form, issue: v})}>
              <SelectTrigger><SelectValue placeholder="Arıza seçin" /></SelectTrigger>
              <SelectContent>
                {issues.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          
          {/* Aftermarket Price - only show if issue has quality option */}
          {hasQualityOption && (
            <div className="space-y-2">
              <Label>Yan Sanayi Fiyatı</Label>
              <Input type="text" placeholder="2500 veya 2500-5000 veya Tespiti Gerekli" value={form.aftermarket_price} onChange={(e) => setForm({...form, aftermarket_price: e.target.value})} />
            </div>
          )}
          
          {/* Original Price - only show if issue has quality option */}
          {hasQualityOption && (
            <div className="space-y-2">
              <Label>Orijinal Fiyatı</Label>
              <Input type="text" placeholder="5000 veya 5000-10000 veya Tespiti Gerekli" value={form.original_price} onChange={(e) => setForm({...form, original_price: e.target.value})} />
            </div>
          )}
          
          {/* Single price for issues without quality option */}
          {!hasQualityOption && form.issue && (
            <div className="space-y-2">
              <Label>Fiyat</Label>
              <Input type="text" placeholder="2500 veya Tespiti Gerekli" value={form.aftermarket_price} onChange={(e) => setForm({...form, aftermarket_price: e.target.value, original_price: e.target.value})} />
            </div>
          )}
        </div>
        
        <Button onClick={savePrice} className="mt-4">
          {isEditing ? <><Save className="w-4 h-4 mr-2" />Güncelle</> : <><Plus className="w-4 h-4 mr-2" />Ekle</>}
        </Button>
      </div>
      
      <div className="bg-card rounded-2xl border p-6">
        <h3 className="font-semibold mb-2">Kayıtlı Fiyatlar ({prices.length})</h3>
        <p className="text-sm text-muted-foreground mb-4">Düzenlemek için satıra tıklayın</p>
        {prices.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">Henüz fiyat eklenmemiş</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">Ürün</th>
                  <th className="text-left p-2">Model</th>
                  <th className="text-left p-2">Arıza</th>
                  <th className="text-right p-2">Yan Sanayi</th>
                  <th className="text-right p-2">Orijinal</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {prices.map((p) => (
                  <tr 
                    key={p.id} 
                    className={`border-b cursor-pointer transition-colors ${form.id === p.id ? 'bg-primary/10' : 'hover:bg-muted/50'}`}
                    onClick={() => editPrice(p)}
                  >
                    <td className="p-2">{p.product_type}</td>
                    <td className="p-2">{p.brand} {p.model}</td>
                    <td className="p-2">{p.issue}</td>
                    <td className="p-2 text-right">{p.aftermarket_price ? (typeof p.aftermarket_price === 'number' ? `${p.aftermarket_price}₺` : p.aftermarket_price) : '-'}</td>
                    <td className="p-2 text-right">{p.original_price ? (typeof p.original_price === 'number' ? `${p.original_price}₺` : p.original_price) : '-'}</td>
                    <td className="p-2" onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="sm" onClick={() => deletePrice(p.id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// Change Password Dialog Component
const ChangePasswordDialog = ({ isOpen, onClose, token, identityForm, setIdentityForm, isVerifying, onSubmit }) => {
  if (!isOpen) return null;
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-primary" />
            Şifre Değiştir
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <p className="text-sm text-muted-foreground">Kimlik bilgilerinizi doğrulayın</p>
          <div className="space-y-2">
            <Label>İsim Soyisim *</Label>
            <Input 
              value={identityForm.name} 
              onChange={(e) => setIdentityForm({...identityForm, name: e.target.value})} 
              placeholder="Lütfen İsim/Soyisim" 
              required 
            />
          </div>
          <div className="space-y-2">
            <Label>TC Kimlik No *</Label>
            <Input 
              value={identityForm.tc} 
              onChange={(e) => setIdentityForm({...identityForm, tc: e.target.value})} 
              placeholder="XXXXXXXXXXX" 
              maxLength={11}
              required 
            />
          </div>
          <div className="space-y-2">
            <Label>Kimlik Seri No *</Label>
            <Input 
              value={identityForm.seri_no} 
              onChange={(e) => setIdentityForm({...identityForm, seri_no: e.target.value})} 
              placeholder="AXXXXXXXX" 
              required 
            />
          </div>
          <div className="space-y-2">
            <Label>Doğum Tarihi *</Label>
            <Input 
              value={identityForm.birth_date} 
              onChange={(e) => setIdentityForm({...identityForm, birth_date: e.target.value})} 
              placeholder="GG.AA.YYYY" 
              required 
            />
          </div>
          <hr className="my-2" />
          <div className="space-y-2">
            <Label>Mevcut Şifre *</Label>
            <Input 
              type="password"
              value={identityForm.old_password} 
              onChange={(e) => setIdentityForm({...identityForm, old_password: e.target.value})} 
              placeholder="Mevcut şifreniz" 
              required 
            />
          </div>
          <div className="space-y-2">
            <Label>Yeni Şifre *</Label>
            <Input 
              type="password"
              value={identityForm.new_password} 
              onChange={(e) => setIdentityForm({...identityForm, new_password: e.target.value})} 
              placeholder="En az 6 karakter" 
              required 
            />
          </div>
          <div className="space-y-2">
            <Label>Yeni Şifre (Tekrar) *</Label>
            <Input 
              type="password"
              value={identityForm.confirm_password} 
              onChange={(e) => setIdentityForm({...identityForm, confirm_password: e.target.value})} 
              placeholder="Şifreyi tekrar girin" 
              required 
            />
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              İptal
            </Button>
            <Button type="submit" className="flex-1" disabled={isVerifying}>
              {isVerifying ? 'Doğrulanıyor...' : 'Şifreyi Değiştir'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPage;
// ===================== YORUM YÖNETİM SAYFASI (ReviewsTab) =====================
const ReviewsTab = ({ reviews = [], token, onRefresh }) => {
  const [form, setForm] = useState({ id: null, author_name: '', text: '', relative_time: '1 hafta önce', rating: 5 });
  const [isEditing, setIsEditing] = useState(false);

  // Yorum Kaydetme (Ekleme veya Güncelleme)
  const saveReview = async () => {
    if (!form.author_name || !form.text) { 
      toast.error('Müşteri adı ve yorum metni boş bırakılamaz'); 
      return; 
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/reviews`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${token}` 
        },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        toast.success(isEditing ? 'Yorum başarıyla güncellendi' : 'Yeni yorum yayına alındı');
        // Formu sıfırla
        setForm({ id: null, author_name: '', text: '', relative_time: '1 hafta önce', rating: 5 });
        setIsEditing(false);
        onRefresh(); // Listeyi tazele
      } else {
        toast.error('Kaydedilirken bir hata oluştu');
      }
    } catch (error) {
      toast.error('Bağlantı hatası');
    }
  };

  // Yorum Silme
  const deleteReview = async (id, e) => {
    e.stopPropagation(); // Satıra tıklama olayını engelle (düzenleme moduna girmesin)
    if (!window.confirm('Bu yorumu vitrinden kaldırmak istediğinize emin misiniz?')) return;

    try {
      const response = await fetch(`${API_URL}/api/admin/reviews/${id}`, { 
        method: 'DELETE', 
        headers: { 'Authorization': `Bearer ${token}` } 
      });
      
      if (response.ok) {
        toast.success('Yorum silindi');
        if (form.id === id) {
           setForm({ id: null, author_name: '', text: '', relative_time: '', rating: 5 });
           setIsEditing(false);
        }
        onRefresh();
      }
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    }
  };

  return (
    <div className="space-y-6">
      {/* Yorum Ekleme/Düzenleme Formu */}
      <div className="bg-card rounded-2xl border p-6 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {isEditing ? <Save className="w-5 h-5 text-primary" /> : <Plus className="w-5 h-5 text-primary" />}
            {isEditing ? 'Yorumu Güncelle' : 'Sitedeki 4 Yorumu Yönet'}
          </h3>
          {isEditing && (
            <Button variant="outline" size="sm" onClick={() => {
              setIsEditing(false);
              setForm({ id: null, author_name: '', text: '', relative_time: '1 hafta önce', rating: 5 });
            }}>İptal</Button>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Müşteri Adı Soyadı</Label>
            <Input 
              value={form.author_name} 
              onChange={(e) => setForm({...form, author_name: e.target.value})} 
              placeholder="Örn: Ahmet Yılmaz"
            />
          </div>
          <div className="space-y-2">
            <Label>Zaman Etiketi</Label>
            <Input 
              value={form.relative_time} 
              onChange={(e) => setForm({...form, relative_time: e.target.value})} 
              placeholder="Örn: 2 gün önce veya 1 ay önce"
            />
          </div>
          <div className="md:col-span-2 space-y-2">
            <Label>Müşteri Yorumu</Label>
            <Textarea 
              value={form.text} 
              onChange={(e) => setForm({...form, text: e.target.value})} 
              placeholder="Google'daki yorumu buraya yapıştırın..."
              rows={3}
            />
          </div>
        </div>
        
        <Button onClick={saveReview} className="mt-6 w-full md:w-auto">
          <Save className="w-4 h-4 mr-2" />
          {isEditing ? 'Değişiklikleri Kaydet' : 'Yorumu Yayınla'}
        </Button>
      </div>
      
      {/* Mevcut Yorumlar Listesi */}
      <div className="bg-card rounded-2xl border p-6 shadow-sm">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Check className="w-5 h-5 text-green-500" />
          Şu An Sitede Görünenler ({reviews.length})
        </h3>
        
        {reviews.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed rounded-xl">
            <p className="text-muted-foreground italic">Henüz yorum eklenmemiş. Yukarıdaki formdan ilk yorumu ekleyebilirsin reis.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {reviews.map((r) => (
              <div 
                key={r.id} 
                onClick={() => { setForm(r); setIsEditing(true); window.scrollTo({top: 0, behavior: 'smooth'}); }}
                className={`p-4 border rounded-xl flex justify-between items-center transition-all cursor-pointer ${form.id === r.id ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'hover:bg-muted/50'}`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-bold text-foreground">{r.author_name}</span>
                    <span className="text-xs text-muted-foreground">• {r.relative_time}</span>
                  </div>
                  <p className="text-sm text-muted-foreground italic line-clamp-2">"{r.text}"</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={(e) => deleteReview(r.id, e)}
                  className="ml-4 hover:bg-destructive/10 hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};